import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSharedState } from "../../../context/formsContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getProperty,
  getNestedProperty,
} from "../../../utils/apiResponseHelper";
import {
  trackFreeTrialSignupComplete,
  trackFreeTrialSignupStep,
} from "../../../utils/analytics";

const MEMBERS_LOGIN_URL = "https://www.ksol.co.uk/contents/members-login";

function useCustomInput(addChildDashboard, sessionCourseId) {
  const {
    value,
    setValue,
    setLoading,
    isGcse,
    setLoginToken,
    verified,
    isValidYear,
    setMentorId,
    setStudentId,
    setMentorEmail,
    captcha,
    prefix,
  } = useSharedState();
  const [errorMessage, setErrorMessage] = useState({});
  const [selectedValue, setSelectedValue] = useState("");
  const [postcodeValid, setPostcodeValid] = useState(false);
  const [addressData, setAddressData] = useState([]);

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (event, inputType) => {
    const newValue = event.target.value;

    setValue((prevValue) => ({
      ...prevValue,
      [inputType]: newValue,
    }));
    validateField(newValue, inputType);
  };

  const validateField = (newValue, inputType) => {
    let error = "";

    switch (inputType) {
      case "phoneNumber":
        error = validatePhoneNumber(newValue);
        break;
      case "postcode":
        error = validatePostcode(newValue);
        break;
      case "password":
        error = validatePassword(newValue);
        break;
      case "email":
        error = validateEmail(newValue);
        break;
      case "confirmedPassword":
        error = validatePasswordMatch(
          { ...value, confirmedPassword: newValue },
          "parent",
        );
        break;
      case "DOB":
        error = validateDOB(newValue);
        break;
      case "childPassword":
        error = validatePassword(newValue);
        break;
      case "childConfirmedPassword":
        error = validatePasswordMatch(
          { ...value, childConfirmedPassword: newValue },
          "child",
        );
        break;
      default:
        break;
    }
    setErrorMessage((prevErrors) => ({
      ...prevErrors,
      [inputType]: error,
    }));
  };

  const validateParentNeededFields = () => {
    if (
      value.title &&
      value.firstName &&
      value.lastName &&
      value.phoneNumber &&
      value.addressLineOne &&
      value.city &&
      value.postcode &&
      value.password &&
      value.confirmedPassword &&
      value.email
    )
      return true;
  };

  const validateChildNeededFields = () => {
    if (
      value.childFirstName &&
      value.childLastName &&
      value.DOB &&
      value.gender &&
      value.childUsername &&
      value.childPassword &&
      value.childConfirmedPassword
    )
      return true;
  };

  const validateDOB = (dob) => {
    const regex = /^\d{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12][0-9]|3[01])$/;

    if (!dob || dob.length === 0) {
      return "Please enter a DOB";
    }

    if (!regex.test(dob)) {
      return "Please enter a valid DOB in format yyyy-mm-dd";
    }

    return "";
  };

  const validatePhoneNumber = (number) => {
    const regex = /^(?:07\d{8,9}|01\d{8,9}|02\d{8,9}|03\d{8,9})$/;

    const numbertest = regex.test(number);

    if (!numbertest) {
      return "Please enter a valid number";
    } else if (typeof number !== "string" || isNaN(number)) {
      return "Please enter a valid number";
    } else if (number.length !== 11) {
      return "Phone number must contain 11 digits";
    } else if (number.length === 0) {
      return "Please enter a number";
    }
    return "";
  };

  const validateTitle = (title) => {
    if (title.length === 0) {
      return "Please select a title";
    }
  };

  const validateFirstName = (firstName) => {
    if (firstName.length === 0) {
      return "Please enter a first name";
    }
  };

  const validateLastName = (lastName) => {
    if (lastName.length === 0) {
      return "Please enter a last name";
    }
  };

  const validateAddressLineOne = (address) => {
    if (Object.keys(address).length === 0) {
      return "Please enter address";
    }
  };

  const validateAddress = (address) => {
    if (address.length === 0) {
      return "Please enter city";
    }
  };

  const validatePostcode = (postcode) => {
    const regex = /^[A-Z]{1,2}[0-9]{1,2} ?[0-9][A-Z]{2}$/i;
    const isValid = regex.test(postcode.replace(/\s/g, ""));
    if (isValid) {
      setPostcodeValid(true);
    } else {
      setPostcodeValid(false);
    }

    if (postcode.length === 0) {
      return "Please enter a postcode";
    }

    return isValid ? "" : "Please enter a valid postcode";
  };

  const validatePassword = (passwordOne) => {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[#?!@$%^&*-]).{8,}$/;
    if (!regex.test(passwordOne)) {
      return "Password must be a minimum of 8 characters, contain a capital letter, a number and special character";
    } else if (passwordOne.length === 0) {
      return "Please enter a password";
    } else {
      return "";
    }
  };

  const validateEmail = (email) => {
    const shouldValidate = (isGcse && addChildDashboard) || !addChildDashboard;

    if (!shouldValidate) return "";

    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

    if (!email || email.trim().length === 0) {
      return "Please enter an email address";
    }

    return regex.test(email) ? "" : "Please enter a valid email address";
  };

  const validatePasswordMatch = (value, age) => {
    if (age === "parent") {
      if (value.password?.trim() !== value.confirmedPassword?.trim()) {
        return "Passwords do not match";
      }
    }
    if (age === "child") {
      if (
        value.childPassword?.trim() !== value.childConfirmedPassword?.trim()
      ) {
        return "Passwords do not match";
      }
    }
    return "";
  };

  const validateEmailExists = async () => {
    try {
      const response = await fetch(
        `${prefix}/api-40/api40/Frontend/GetMentorByEmail?Email=${value.email}`,
        {
          headers: {
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();

      if (data === "Email Exists") {
        setErrorMessage((prevErrors) => ({
          ...prevErrors,
          email: "Email already exists, please log in",
        }));
        return "Email already exists, please log in";
      } else {
        setErrorMessage({});
        return "";
      }
    } catch (error) {
      setErrorMessage((prevErrors) => ({
        ...prevErrors,
        email: "An error occurred while checking the email",
      }));
      return true;
    }
  };

  const validateChildFirstName = (firstName) => {
    if (firstName.length === 0) {
      return "Please enter first name";
    }
  };

  const validateChildLastName = (lastName) => {
    if (lastName.length === 0) {
      return "Please enter first name";
    }
  };

  const validateGender = (gender) => {
    if (gender.length === 0) {
      return "Please select a gender";
    }
  };

  const validateChildUsername = async (username) => {
    if (username.length === 0) {
      return "Please enter a username";
    }

    try {
      const response = await fetch(
        `${prefix}/api-40/api40/Registration/CheckStudentUsername?Username=${username}`,
        {
          headers: {
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();

      if (data === "Y") {
        return "Username already exists, please use a different username";
      }

      return "";
    } catch (error) {
      return "An error occurred while validating the username";
    }
  };

  const nextClick = async () => {
    setLoading(true);
    const allNeededFields = validateParentNeededFields();

    const emailError = validateEmail(value.email);
    const emailExistsResult = await validateEmailExists();
    const emailExistsError =
      emailExistsResult === true
        ? "An error occurred while checking the email"
        : emailExistsResult || "";
    const phoneError = validatePhoneNumber(value.phoneNumber);
    const postcodeError = validatePostcode(value.postcode);
    const passwordError = validatePassword(value.password);
    const titleError = validateTitle(value.title);
    const firstNameError = validateFirstName(value.firstName);
    const lastNameError = validateLastName(value.lastName);
    const addressLineOneError = validateAddressLineOne(value.addressLineOne);
    const addressCityError = validateAddress(value.city);

    setErrorMessage({
      email: emailError || emailExistsError,
      phoneNumber: phoneError,
      postcode: postcodeError,
      password: passwordError,
      title: titleError,
      firstName: firstNameError,
      lastName: lastNameError,
      addressLineOne: addressLineOneError,
      city: addressCityError,
      confirmedPassword: validatePasswordMatch(
        value.password,
        value.confirmedPassword,
      ),
    });

    if (emailExistsError === "Email already exists, please log in") {
      setLoading(false);
      window.location.assign(MEMBERS_LOGIN_URL);
      return;
    }

    if (
      !allNeededFields ||
      emailError ||
      emailExistsError ||
      phoneError ||
      postcodeError ||
      passwordError ||
      titleError ||
      firstNameError ||
      lastNameError ||
      addressLineOneError ||
      addressCityError
    ) {
      setLoading(false);
      return;
    }

    trackFreeTrialSignupStep("parent_details", 4);
    navigate("/studentRegistration");
    setLoading(false);
  };

  const runChildValidations = async () => {
    const allNeededFields = validateChildNeededFields();
    const dobError = validateDOB(value.DOB);
    const passwordError = validatePassword(value.childPassword);
    const usernameError = await validateChildUsername(value.childUsername);
    const firstNameError = validateChildFirstName(value.childFirstName);
    const lastNameError = validateChildLastName(value.childLastName);
    const genderError = validateGender(value.gender);
    const confirmError = validatePasswordMatch(
      value.childPassword,
      value.childConfirmedPassword,
    );

    let emailError = "";
    if (isGcse || sessionCourseId) {
      emailError = validateEmail(value.studentEmail);
    }

    setErrorMessage({
      DOB: dobError,
      childFirstName: firstNameError,
      childLastName: lastNameError,
      gender: genderError,
      childPassword: passwordError,
      childConfirmedPassword: confirmError,
      childUsername: usernameError,
      ...(isGcse || (sessionCourseId ? { studentEmail: emailError } : {})),
    });

    const hasError =
      !allNeededFields ||
      dobError ||
      passwordError ||
      usernameError ||
      firstNameError ||
      lastNameError ||
      genderError ||
      emailError;

    return hasError;
  };

  const getSchoolYearFromDob = (dob) => {
    if (!dob) return null;

    const [childYear, childMonth, childDay] = dob.split("-").map(Number);
    if (!childYear || !childMonth || !childDay) return null;

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();

    const isBeforeSeptember =
      currentMonth < 9 || (currentMonth === 9 && currentDay < 1);
    const currentAcademicYear = isBeforeSeptember
      ? currentYear - 1
      : currentYear;

    const schoolStartYear =
      childMonth > 9 || (childMonth === 9 && childDay >= 1)
        ? childYear + 5
        : childYear + 4;

    return currentAcademicYear - schoolStartYear;
  };

  const getProductIdBySchoolYear = (dob) => {
    const schoolYear = getSchoolYearFromDob(dob);
    if (schoolYear === 3) return 372;
    if (schoolYear === 4) return 15;
    if (schoolYear === 5) return 14;
    return null;
  };

  const nextClickChild = async () => {
    const hasError = await runChildValidations();

    if (hasError) {
      setLoading(false);
      return;
    }

    if (!addChildDashboard && !captcha) {
      return;
    }

    if (!addChildDashboard) {
      trackFreeTrialSignupStep("child_details", 5);
    }

    try {
      setLoading(true);
      const productId = getProductIdBySchoolYear(value.DOB);
      const productIdQuery = productId ? `&ProductId=${productId}` : "";

      if (!addChildDashboard) {
        const response = await fetch(
          `${prefix}/api-40/api40/Registration/InsertMentor?Title=${value.title}&FirstName=${value.firstName}&LastName=${value.lastName}&Address1=${value.addressLineOne}&Address2=${value.addressLineTwo}&Town=${value.city}&PostCode=${value.postcode}&Country=UK&Email=${value.email}&Mobile=${value.phoneNumber}&Password=${value.password}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
            },
          },
        );

        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const studentResponse = await fetch(
          `${prefix}/api-40/api40/Registration/InsertStudent?FirstName=${value.childFirstName}&LastName=${value.childLastName}&Gender=${value.gender}&DOB=${value.DOB}&Username=${value.childUsername}&ProspectiveSchool=${value.school}&Password=${value.childPassword}&MentorUsername=${value.email}${productIdQuery}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
            },
          },
        );

        if (!studentResponse.ok)
          throw new Error(`HTTP error! Status: ${studentResponse.status}`);

        trackFreeTrialSignupComplete();

        // Check if email is already verified (e.g., from free downloads account)
        let isEmailVerified = false;
        try {
          const verifyCheckResponse = await fetch(
            `${prefix}/api-40/api40/Registration/GetVerifiedEmail?Email=${encodeURIComponent(value.email.trim())}`,
            {
              headers: {
                "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
              },
            },
          );
          if (verifyCheckResponse.ok) {
            const verifyData = await verifyCheckResponse.json();
            const verified =
              getProperty(verifyData[0] || verifyData, "verified") ||
              verifyData[0]?.Verified ||
              verifyData[0]?.verified ||
              verifyData?.Verified ||
              verifyData?.verified;
            if (verifyData && verifyData[0] && verified === "Y") {
              isEmailVerified = true;
            }
          }
        } catch (verifyCheckError) {}

        setLoading(false);

        if (isEmailVerified) {
          setShowRegistrationSuccess(true);
          setTimeout(() => {
            window.location.assign(MEMBERS_LOGIN_URL);
          }, 2500);
        } else {
          // Email not verified - send verification email and navigate to verification screen
          try {
            const verifyResponse = await fetch(
              `${prefix}/api-40/api40/Registration/EmailVerifcationCode?Email=${encodeURIComponent(value.email.trim())}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
                },
              },
            );
            if (!verifyResponse.ok) {
            }
          } catch (verifyError) {}
          navigate("/verification");
        }
      } else {
        setLoading(true);

        const mentorEmail = localStorage.getItem("mentorEmail");

        const studentResponse = await fetch(
          `${prefix}/api-40/api40/Registration/InsertStudent?FirstName=${value.childFirstName}&LastName=${value.childLastName}&Gender=${value.gender}&DOB=${value.DOB}&Username=${value.childUsername}&ProspectiveSchool=${value.school}&Password=${value.childPassword}&MentorUsername=${mentorEmail}${productIdQuery}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9",
            },
          },
        );

        if (!studentResponse.ok)
          throw new Error(`HTTP error! Status: ${studentResponse.status}`);

        setLoading(false);

        toast.success("Child added successfully!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    errorMessage,
    validatePhoneNumber,
    handleInputChange,
    validateEmailExists,
    selectedValue,
    setSelectedValue,
    validateField,
    value,
    nextClick,
    nextClickChild,
    postcodeValid,
    addressData,
    showCaptcha,
    setShowCaptcha,
    showRegistrationSuccess,
  };
}

export default useCustomInput;
